import type { RefObject, ReactNode } from 'react'
import type { RadioButtonGroupValue } from '@/components/form/RadioButtonGroup'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { Collection, CollectionIdInput } from '@/lib/services/collections/collections.types'
import type {
  ProductCatalogPrintFormValues,
  ProductCatalogPrintPreset,
} from '@/lib/services/product-catalog-print/productCatalogPrint.schema'

import FieldInfo from '@/components/form/FieldInfo'
import { LoadingState } from '@/components/patterns/loading-state'
import ProductCollectionPicker from '@/components/products/ProductCollectionPicker'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { useProductCatalogPrintPreview } from '@/lib/services/product-catalog-print/productCatalogPrint.query'
import {
  getProductCatalogPrintDefaults,
  productCatalogPrintPresets,
  productCatalogPrintSchema,
  toProductCatalogPrintPayload,
} from '@/lib/services/product-catalog-print/productCatalogPrint.schema'
import { getErrorMessage } from '@/lib/utils'
import { BookOpen, FileText, LibraryBig, Printer, RefreshCw, ZoomIn, ZoomOut } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

// These methods are emitted by print/product-catalog.blade.php after its document loads.
interface CatalogPreviewApi {
  setCatalogPreviewScale?: (scale: number) => void
  prepareCatalogPreviewLayout?: () => void
  waitForCatalogAssets?: () => Promise<unknown>
  prepareCatalogPrintLayout?: () => Promise<void>
}

interface CatalogPreviewFrame extends HTMLIFrameElement {
  readonly contentWindow: (Window & CatalogPreviewApi) | null
}

type CatalogFrameRef = RefObject<CatalogPreviewFrame | null>
type CatalogCollection = Pick<Collection, 'name'> & { id: CollectionIdInput }

interface CatalogPosition {
  productIndex: number
  productId: string | null
  productOffset: number | null
  productOrder: string | null
  sectionKey: string | null
  scrollLeft: number
  scrollTop: number
}

const presetOptions = [
  { id: 'customer_catalog', name: 'Customer Catalog', start: <FileText className="size-4" /> },
  { id: 'recipe_book', name: 'Recipe Book', start: <BookOpen className="size-4" /> },
] satisfies { id: ProductCatalogPrintPreset; name: string; start: ReactNode }[]

const sourceOptions = [
  { id: 'all', name: 'All Products' },
  { id: 'collections', name: 'Collections' },
]

const imageSizeOptions = [
  { id: 'small', name: 'Small' },
  { id: 'medium', name: 'Medium' },
  { id: 'large', name: 'Large' },
]

const layoutOptions = [
  { id: 'rows', name: 'Rows' },
  { id: 'full_page', name: 'Full page' },
]

const pageFillOptions = [
  { id: 'preserve_order', name: 'Keep order' },
  { id: 'optimize', name: 'Fill pages' },
]

const catalogPageWidth = 8.5 * 96
const catalogPreviewHeight = 11.6 * 96
const catalogPreviewInset = 24
const catalogPreviewMinScale = 0.25
const catalogPreviewMaxScale = 1.5
const catalogPreviewScaleStep = 0.1

function captureCatalogPosition(frame: CatalogPreviewFrame | null): CatalogPosition | null {
  const document = frame?.contentDocument
  const contentWindow = frame?.contentWindow

  if (!document || !contentWindow) return null

  const products = Array.from(document.querySelectorAll<HTMLElement>('.product-card'))
  const productIndex = products.findIndex((product) => product.getBoundingClientRect().bottom > 0)
  const product = productIndex >= 0 ? products[productIndex] : null

  return {
    productIndex,
    productId: product?.dataset.productId ?? null,
    productOffset: product?.getBoundingClientRect().top ?? null,
    productOrder: product?.dataset.productOrder ?? null,
    sectionKey: product?.closest<HTMLElement>('.catalog-section')?.dataset.sectionKey ?? null,
    scrollLeft: contentWindow.scrollX,
    scrollTop: contentWindow.scrollY,
  }
}

function restoreCatalogPosition(
  frame: CatalogPreviewFrame | null,
  position: CatalogPosition | null
): number | null {
  const document = frame?.contentDocument
  const contentWindow = frame?.contentWindow

  if (!document || !contentWindow || !position) return null

  const products = Array.from(document.querySelectorAll<HTMLElement>('.product-card'))
  const matchedProduct = products.find(
    (product) =>
      product.closest<HTMLElement>('.catalog-section')?.dataset.sectionKey ===
        position.sectionKey &&
      (position.productId !== null
        ? product.dataset.productId === position.productId
        : position.productOrder !== null && product.dataset.productOrder === position.productOrder)
  )
  const product =
    matchedProduct ?? (position.productIndex >= 0 ? products[position.productIndex] : null)
  const top = product
    ? contentWindow.scrollY + product.getBoundingClientRect().top - Number(position.productOffset)
    : position.scrollTop

  contentWindow.scrollTo(position.scrollLeft, Math.max(0, top))

  return Math.max(0, top)
}

function scrollCatalogPreview(event: WheelEvent, frame: CatalogPreviewFrame | null) {
  const contentWindow = frame?.contentWindow
  const document = frame?.contentDocument

  if (!contentWindow || !document || event.ctrlKey || event.deltaY === 0) return

  const scrollHeight =
    document.scrollingElement?.scrollHeight ?? document.documentElement.scrollHeight
  const viewportHeight = contentWindow.innerHeight || document.documentElement.clientHeight
  const maxScrollTop = Math.max(0, scrollHeight - viewportHeight)
  const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewportHeight : 1
  const nextScrollTop = Math.max(
    0,
    Math.min(maxScrollTop, contentWindow.scrollY + event.deltaY * deltaMultiplier)
  )

  event.preventDefault()

  if (nextScrollTop === contentWindow.scrollY) return

  contentWindow.scrollTo(contentWindow.scrollX, nextScrollTop)
}

function scrollPreviewUnderPointer(event: WheelEvent, frame: CatalogPreviewFrame | null) {
  if (!frame) return

  const bounds = frame.getBoundingClientRect()
  const isOverPreview =
    event.clientX >= bounds.left &&
    event.clientX <= bounds.right &&
    event.clientY >= bounds.top &&
    event.clientY <= bounds.bottom

  if (isOverPreview) {
    scrollCatalogPreview(event, frame)
  }
}

function scaleCatalogPreview(
  frame: CatalogPreviewFrame | null,
  requestedScale: number | null = null
): number | null {
  const contentWindow = frame?.contentWindow

  if (!frame || !contentWindow?.setCatalogPreviewScale) return null

  const bounds = frame.getBoundingClientRect()
  const viewportWidth = frame.clientWidth || bounds.width
  const viewportHeight = frame.clientHeight || bounds.height

  if (viewportWidth <= 0 || viewportHeight <= 0) return null

  const fittedScale = Math.max(
    catalogPreviewMinScale,
    Math.min(
      1,
      (viewportWidth - catalogPreviewInset) / catalogPageWidth,
      (viewportHeight - catalogPreviewInset) / catalogPreviewHeight
    )
  )
  const scale = Number(
    Math.max(
      catalogPreviewMinScale,
      Math.min(catalogPreviewMaxScale, requestedScale ?? fittedScale)
    ).toFixed(4)
  )

  contentWindow.setCatalogPreviewScale(scale)

  return scale
}

function updateCatalogPreview(frame: CatalogPreviewFrame | null, html: string) {
  const currentDocument = frame?.contentDocument

  if (!currentDocument || !frame) return null

  const nextDocument = new DOMParser().parseFromString(html, 'text/html')
  const currentCatalog = currentDocument.querySelector('.catalog-document')
  const nextCatalog = nextDocument.querySelector('.catalog-document')
  const currentStyle = currentDocument.head.querySelector('style')
  const nextStyle = nextDocument.head.querySelector('style')

  if (!currentCatalog || !nextCatalog || !currentStyle || !nextStyle) return null

  const position = captureCatalogPosition(frame)
  const currentFontStylesheet =
    currentDocument.head.querySelector<HTMLLinkElement>('link[rel="stylesheet"]')
  const nextFontStylesheet =
    nextDocument.head.querySelector<HTMLLinkElement>('link[rel="stylesheet"]')

  currentDocument.title = nextDocument.title
  currentStyle.textContent = nextStyle.textContent

  if (currentFontStylesheet?.href !== nextFontStylesheet?.href) {
    currentFontStylesheet?.remove()

    if (nextFontStylesheet) {
      currentDocument.head.append(currentDocument.importNode(nextFontStylesheet, true))
    }
  }

  Array.from(currentDocument.body.attributes).forEach((attribute) => {
    currentDocument.body.removeAttribute(attribute.name)
  })
  Array.from(nextDocument.body.attributes).forEach((attribute) => {
    currentDocument.body.setAttribute(attribute.name, attribute.value)
  })

  currentCatalog.replaceWith(currentDocument.importNode(nextCatalog, true))
  frame.contentWindow?.prepareCatalogPreviewLayout?.()

  return position
}

function ProductCatalogPreviewFrame({
  frameRef,
  html,
  requestedScale,
  onScaleChange,
}: {
  frameRef: CatalogFrameRef
  html: string
  requestedScale: number | null
  onScaleChange: (scale: number) => void
}) {
  const [initialHtml] = useState(html)
  const appliedHtml = useRef(html)

  useEffect(() => {
    const frame = frameRef.current
    let previewDocument: Document | null | undefined

    const handleWheel = (event: WheelEvent) => scrollCatalogPreview(event, frame)
    const attachWheelHandler = () => {
      previewDocument?.removeEventListener?.('wheel', handleWheel, { capture: true })
      previewDocument = frame?.contentDocument
      previewDocument?.addEventListener?.('wheel', handleWheel, {
        capture: true,
        passive: false,
      })
    }

    const applyScale = () => {
      const scale = scaleCatalogPreview(frame, requestedScale)

      if (scale !== null) {
        onScaleChange(scale)
      }
    }

    const handleLoad = () => {
      attachWheelHandler()
      applyScale()
    }

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(applyScale)

    frame?.addEventListener('load', handleLoad)
    if (frame) resizeObserver?.observe(frame)
    attachWheelHandler()
    applyScale()

    return () => {
      frame?.removeEventListener('load', handleLoad)
      resizeObserver?.disconnect()
      previewDocument?.removeEventListener?.('wheel', handleWheel, { capture: true })
    }
  }, [frameRef, onScaleChange, requestedScale])

  // Keep the iframe mounted so refreshed preview HTML cannot reset its scroll position.
  useEffect(() => {
    if (appliedHtml.current === html) return

    const frame = frameRef.current
    let cancelled = false
    let animationFrame: number | undefined

    const settlePreviewLayout = async () => {
      const waitForAssets = frame?.contentWindow?.waitForCatalogAssets

      if (!waitForAssets) return

      await waitForAssets()

      if (cancelled) return

      const position = captureCatalogPosition(frame)

      frame.contentWindow?.prepareCatalogPreviewLayout?.()
      restoreCatalogPosition(frame, position)
    }

    const applyUpdate = () => {
      if (cancelled) return

      const position = updateCatalogPreview(frame, html)

      if (!position) return

      appliedHtml.current = html
      const restoredTop = restoreCatalogPosition(frame, position)

      animationFrame = window.requestAnimationFrame(() => {
        if (cancelled) return

        const contentWindow = frame?.contentWindow

        if (
          contentWindow &&
          restoredTop !== null &&
          Math.abs(contentWindow.scrollY - restoredTop) <= 2
        ) {
          contentWindow.prepareCatalogPreviewLayout?.()
          restoreCatalogPosition(frame, position)
        }
      })

      void settlePreviewLayout()
    }

    if (frame?.contentDocument?.readyState === 'loading') {
      frame.addEventListener('load', applyUpdate, { once: true })
    } else {
      applyUpdate()
    }

    return () => {
      cancelled = true
      frame?.removeEventListener('load', applyUpdate)

      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame)
      }
    }
  }, [frameRef, html])

  return (
    <iframe
      ref={frameRef}
      title="Printable catalog preview"
      srcDoc={initialHtml}
      className="h-[calc(100dvh-15rem)] min-h-[36rem] w-full rounded-lg border-0 bg-white xl:h-full xl:min-h-0"
    />
  )
}

function ProductCatalogPreview({
  frameRef,
  organizationId,
  values,
}: {
  frameRef: CatalogFrameRef
  organizationId?: OrganizationIdInput
  values: ProductCatalogPrintFormValues
}) {
  const [requestedScale, setRequestedScale] = useState<number | null>(null)
  const [previewScale, setPreviewScale] = useState<number | null>(null)
  const validation = productCatalogPrintSchema.safeParse(values)
  const payload = validation.success ? toProductCatalogPrintPayload(validation.data) : null
  const { data, error, isFetching, isPending, refetch } = useProductCatalogPrintPreview({
    organizationId,
    values: payload,
    enabled: Boolean(payload),
  })

  const printCatalog = async () => {
    try {
      const printWindow = frameRef.current?.contentWindow

      if (!printWindow?.prepareCatalogPrintLayout) {
        throw new Error('The catalog preview is not ready yet.')
      }

      await printWindow.prepareCatalogPrintLayout()
      printWindow.focus()
      printWindow.print()
    } catch (printError) {
      toast.error('Catalog could not be printed', {
        description: getErrorMessage(printError),
      })
    }
  }

  const changePreviewScale = (change: number) => {
    setRequestedScale((currentScale) => {
      const startingScale = currentScale ?? previewScale

      if (startingScale === null) return null

      return Number(
        Math.max(
          catalogPreviewMinScale,
          Math.min(catalogPreviewMaxScale, startingScale + change)
        ).toFixed(4)
      )
    })
  }

  const canZoomOut = previewScale !== null && previewScale > catalogPreviewMinScale
  const canZoomIn = previewScale !== null && previewScale < catalogPreviewMaxScale

  return (
    <Card
      size="flush"
      surface="preview"
      className="min-h-[42rem] overflow-hidden xl:h-full xl:min-h-0"
    >
      <CardHeader
        size="sm"
        className="flex min-h-16 flex-row flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-zinc-950 py-3"
      >
        <div className="min-w-48 flex-1 text-white">
          <Typography variant="sectionTitle" tone="inherit">
            Catalog preview
          </Typography>
          <Typography variant="supporting" tone="inherit" className="mt-0.5 block opacity-70">
            The printed PDF will use US Letter pages.
          </Typography>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <div
            role="group"
            aria-label="Preview zoom"
            className="flex items-center gap-1 text-white"
          >
            <Button
              type="button"
              variant="onDark"
              size="icon-xs"
              aria-label="Zoom out"
              onClick={() => changePreviewScale(-catalogPreviewScaleStep)}
              disabled={!canZoomOut}
            >
              <ZoomOut className="size-4" />
            </Button>
            <Typography
              variant="caption"
              tone="inherit"
              align="center"
              className="w-10 tabular-nums"
            >
              {previewScale === null ? 'Fit' : `${Math.round(previewScale * 100)}%`}
            </Typography>
            <Button
              type="button"
              variant="onDark"
              size="icon-xs"
              aria-label="Zoom in"
              onClick={() => changePreviewScale(catalogPreviewScaleStep)}
              disabled={!canZoomIn}
            >
              <ZoomIn className="size-4" />
            </Button>
            <Button
              type="button"
              variant="onDark"
              size="compact"
              onClick={() => setRequestedScale(null)}
              disabled={requestedScale === null}
            >
              Fit
            </Button>
          </div>
          <Separator orientation="vertical" className="mx-1 h-6! opacity-50" />
          <Button
            type="button"
            onClick={() => {
              void printCatalog()
            }}
            disabled={!data || isPending || isFetching || !payload}
            className="shrink-0"
          >
            <Printer className="size-4" />
            Print
          </Button>
        </div>
      </CardHeader>

      <CardContent
        size="sm"
        className="relative flex min-h-[38rem] items-start justify-center p-3 xl:min-h-0 xl:flex-1"
      >
        {!payload ? (
          <Alert variant="warning" className="m-auto max-w-md">
            <AlertTitle>Choose at least one collection</AlertTitle>
            <AlertDescription>
              Select a collection to build this version of the catalog.
            </AlertDescription>
          </Alert>
        ) : null}

        {payload && isPending ? (
          <LoadingState
            label="Building catalog preview..."
            size="spacious"
            surface="transparent"
            className="m-auto w-full max-w-md border-white/15 text-zinc-300"
          />
        ) : null}

        {payload && error && !data ? (
          <Alert variant="destructive" className="m-auto max-w-md">
            <AlertTitle>Preview could not be loaded</AlertTitle>
            <AlertDescription>
              <p>{getErrorMessage(error)}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetch()
                }}
              >
                <RefreshCw className="size-4" />
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {data && payload ? (
          <>
            {isFetching ? (
              <Badge
                variant="info"
                size="status"
                elevation="raised"
                className="absolute right-5 top-5 z-10"
              >
                Updating preview...
              </Badge>
            ) : null}
            {error ? (
              <Alert variant="destructive" className="absolute left-5 right-5 top-5 z-10">
                <AlertTitle>Preview update failed</AlertTitle>
                <AlertDescription>
                  <p>{getErrorMessage(error)}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void refetch()
                    }}
                  >
                    <RefreshCw className="size-4" />
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}
            <ProductCatalogPreviewFrame
              frameRef={frameRef}
              html={data}
              requestedScale={requestedScale}
              onScaleChange={setPreviewScale}
            />
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

export default function ProductCatalogBuilder({
  organizationId,
  initialCollections = [],
  canListCollections = true,
}: {
  organizationId?: OrganizationIdInput
  initialCollections?: CatalogCollection[]
  canListCollections?: boolean
}) {
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false)
  const [selectedCollections, setSelectedCollections] = useState(initialCollections)
  const previewFrameRef = useRef<CatalogPreviewFrame | null>(null)
  const form = useAppForm({
    defaultValues: getProductCatalogPrintDefaults(initialCollections),
  })

  useEffect(() => {
    if (collectionPickerOpen) return

    const handleWheel = (event: WheelEvent) =>
      scrollPreviewUnderPointer(event, previewFrameRef.current)

    document.addEventListener('wheel', handleWheel, { capture: true, passive: false })

    return () => document.removeEventListener('wheel', handleWheel, { capture: true })
  }, [collectionPickerOpen])

  const applyPreset = (preset: ProductCatalogPrintPreset) => {
    form.setFieldValue('preset', preset)

    Object.entries(productCatalogPrintPresets[preset]).forEach(([field, value]) => {
      // @ts-expect-error -- Object.entries erases the keys of the owner-checked preset fields; each entry is a valid form field/value.
      form.setFieldValue(field, value)
    })
  }

  const applyCollections = (collections: CatalogCollection[]) => {
    setSelectedCollections(collections)
    form.setFieldValue(
      'collection_ids',
      collections.map((collection) => Number(collection.id))
    )
    setCollectionPickerOpen(false)
  }

  const selectSource = (source: RadioButtonGroupValue) => {
    if (source === 'collections' && selectedCollections.length === 0) {
      setCollectionPickerOpen(true)
    }
  }

  return (
    <>
      <form.AppForm>
        <div className="grid min-w-0 gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[22rem_minmax(0,1fr)] xl:items-stretch">
          <Card
            className="min-w-0 xl:h-full xl:min-h-0 xl:overflow-hidden"
            size="sm"
            surface="elevated"
          >
            <CardHeader size="sm">
              <Typography variant="sectionTitle">Catalog settings</Typography>
            </CardHeader>
            <CardContent
              size="sm"
              className="pretty-scrollbar space-y-5 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-contain xl:[scrollbar-gutter:stable]"
            >
              <section className="space-y-3">
                <Typography variant="labelStrong">Format</Typography>
                <form.AppField name="preset">
                  {(field) => (
                    <field.RadioButtonGroup
                      aria-label="Catalog format"
                      appearance="selectorCard"
                      joined={false}
                      options={presetOptions}
                      onValueChange={(value) => {
                        const preset = presetOptions.find((option) => option.id === value)?.id
                        if (preset) applyPreset(preset)
                      }}
                      classNames={{ container: 'grid w-full gap-2', button: 'w-full' }}
                    />
                  )}
                </form.AppField>
              </section>

              <Separator />

              <section className="space-y-3">
                <Typography variant="labelStrong">Products</Typography>
                <form.AppField name="source">
                  {(field) => (
                    <field.RadioButtonGroup
                      aria-label="Print from"
                      options={
                        canListCollections
                          ? sourceOptions
                          : sourceOptions.filter((option) => option.id === 'all')
                      }
                      onValueChange={selectSource}
                      classNames={{ container: 'w-full', button: 'flex-1' }}
                    />
                  )}
                </form.AppField>

                <form.Subscribe selector={(state) => state.values.source}>
                  {(source) =>
                    source === 'collections' ? (
                      <Card size="flush" surface="subtle">
                        <CardContent size="sm" className="space-y-3 py-3">
                          <div className="flex items-center justify-between gap-2">
                            <Typography variant="labelStrong">Collections</Typography>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setCollectionPickerOpen(true)}
                            >
                              <LibraryBig className="size-4" />
                              Choose
                            </Button>
                          </div>

                          {selectedCollections.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {selectedCollections.map((collection) => (
                                <Badge key={collection.id} variant="secondary">
                                  {collection.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Typography variant="supporting">No collections selected.</Typography>
                          )}

                          <form.AppField name="collection_ids">{() => <FieldInfo />}</form.AppField>
                        </CardContent>
                      </Card>
                    ) : null
                  }
                </form.Subscribe>
              </section>

              <Separator />

              <section className="space-y-3">
                <Typography variant="labelStrong">Details</Typography>
                <div className="grid gap-3">
                  <form.AppField name="include_description">
                    {(field) => <field.CheckboxInput label="Descriptions" />}
                  </form.AppField>
                  <form.AppField name="include_recipes">
                    {(field) => <field.CheckboxInput label="Recipes" />}
                  </form.AppField>
                  <form.AppField name="include_prices">
                    {(field) => <field.CheckboxInput label="Prices" />}
                  </form.AppField>
                  <form.AppField name="include_cover">
                    {(field) => <field.CheckboxInput label="Cover page" />}
                  </form.AppField>
                </div>
              </section>

              <Separator />

              <form.AppField name="layout">
                {(field) => (
                  <field.RadioButtonGroup
                    label="Layout"
                    options={layoutOptions}
                    classNames={{ container: 'w-full', button: 'flex-1' }}
                  />
                )}
              </form.AppField>

              <form.Subscribe selector={(state) => state.values.layout}>
                {(layout) =>
                  layout === 'rows' ? (
                    <div className="space-y-2">
                      <form.AppField name="page_fill">
                        {(field) => (
                          <field.RadioButtonGroup
                            label="Page filling"
                            options={pageFillOptions}
                            classNames={{ container: 'w-full', button: 'flex-1' }}
                          />
                        )}
                      </form.AppField>
                      <Typography variant="supporting">
                        Fill pages may slightly reorder products within each collection.
                      </Typography>
                    </div>
                  ) : null
                }
              </form.Subscribe>

              <Separator />

              <form.AppField name="image_size">
                {(field) => (
                  <field.RadioButtonGroup
                    label="Image size"
                    options={imageSizeOptions}
                    classNames={{ container: 'w-full', button: 'flex-1' }}
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>

          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <ProductCatalogPreview
                frameRef={previewFrameRef}
                organizationId={organizationId}
                values={values}
              />
            )}
          </form.Subscribe>
        </div>
      </form.AppForm>

      <Dialog open={collectionPickerOpen} onOpenChange={setCollectionPickerOpen}>
        <DialogContent size="2xlarge" viewport="contained">
          <DialogHeader>
            <DialogTitle>Choose Collections</DialogTitle>
            <DialogDescription>
              Products shared by multiple collections will appear in each selected section.
            </DialogDescription>
          </DialogHeader>
          <ProductCollectionPicker
            organizationId={organizationId}
            initialData={selectedCollections}
            collectionMembershipType={null}
            onConfirm={applyCollections}
            onCancel={() => setCollectionPickerOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
